import { useWatch } from "react-hook-form";
import { useProductsByIds } from "../../../services/productsService";

import { AgreementKindEnum } from "../../../schemas/agreements";
import { isReservationPayment, isFinalSettlementPayment } from "../../../services/agreementsService";
import { properUseCustomersByIds } from "../../../services/customersService";
import { calculateTotalPercentagePerProduct, calculateTotalPricePerProduct } from "./utils";
import { roundToDecimalPlaces } from "../../../helpers/methods";
import { getDiscountedPricesForProduct } from "../../../utils";

/**
 * Check if shares are valid:
 * - Customer ids list matches exactly the list of customers having shares
 * - Each customer appears in exactly one share
 * - The sum of all share percentages is exactly 100
 *
 * Share percentages are integers between 0 and 100
 */
const areSharesValid = (shares, customerIds) => {
    const allShareCustomerIds = shares.flatMap((s) => s.customerIds);
    const uniqueShareCustomerIds = Array.from(new Set(allShareCustomerIds));

    const allCustomersCovered =
        customerIds.length === uniqueShareCustomerIds.length &&
        customerIds.every((id) => uniqueShareCustomerIds.includes(id));

    const eachCustomerInOneShare = allShareCustomerIds.length === uniqueShareCustomerIds.length;

    const sumOfPercentages = shares.reduce((sum, s) => sum + s.percentage, 0);

    const everySharePercentageValid = shares.every((s) => s.percentage > 0 && s.percentage <= 100);

    return (
        allCustomersCovered &&
        eachCustomerInOneShare &&
        sumOfPercentages === 100 &&
        everySharePercentageValid
    );
};

const customerHasRequiredInfo = (customer) => {
    if (customer.person) {
        return personHasRequiredInfo(customer.person);
    }
    if (customer.company) {
        return companyHasRequiredInfo(customer.company);
    }
    return false;
};

const personHasRequiredInfo = (person) =>
    person.has_basic_info && person.has_detailed_info && person.has_id_documentation;

const companyHasRequiredInfo = (company) => company.has_basic_info;

export const isSinglePaymentValid = (payment) => {
    const isValidDueDate = payment.agrepay_due_date != null;
    const isValidEvent = payment.agrepay_milestone != null && payment.agrepay_payment_shift != null;
    const allPercentagesValid = payment.per_product_percentages.every(
        (pp) => pp.percentage >= 0 && pp.percentage <= 100,
    );

    const isValid = (isValidDueDate || isValidEvent) && allPercentagesValid;

    return {
        isValid,
        isValidDueDate,
        isValidEvent,
        allPercentagesValid,
    };
};

// TODO Old solution
// export const arePaymentsValid = (payments) => {
//     const isAtLeastOneTranche = payments.filter((p) => !isReservationPayment(p) && !isFinalSettlementPayment(p)).length > 0;

//     const totalPercentagePerProduct = calculateTotalPercentagePerProduct(payments);

//     const isEachProductCovered = Object.values(totalPercentagePerProduct).every(
//         (percentage) => percentage === 100,
//     );
//     const areIndividualPaymentsValid = payments.filter(p=>!isFinalSettlementPayment(p)).every((p) => isSinglePaymentValid(p).isValid);

//     const isValid = isAtLeastOneTranche && isEachProductCovered && areIndividualPaymentsValid;

//     return {
//         isValid,
//         isEachProductCovered,
//         areIndividualPaymentsValid,
//         isAtLeastOneTranche,
//     };
// };


export const arePaymentsValid = (payments, products, productDiscounts) => {
    if (!payments || !products || !productDiscounts) {
        return false
    }

    const isAtLeastOneTranche = payments.filter((p) => !isReservationPayment(p) && !isFinalSettlementPayment(p)).length > 0;

    const totalPricePerProduct = calculateTotalPricePerProduct(payments, products, productDiscounts)
    let isEachProductCovered = true

    const productsMap = products.reduce((map, product) => {
            map[product.id] = product;
            return map;
    }, {})

    for (const [productId, grossAmount] of Object.entries(totalPricePerProduct)) {
        const product = productsMap[productId];
        if (!product) {
            isEachProductCovered = false;
            break;
        }

        const discount = productDiscounts?.[productId] ?? 0;
        const pricing = getDiscountedPricesForProduct(product, discount);

        if (roundToDecimalPlaces(pricing.gross, 2) !== roundToDecimalPlaces(grossAmount, 2)) {
            isEachProductCovered = false;
            break;
        }
    }

    // TODO Old solution
    // const totalPercentagePerProduct = calculateTotalPercentagePerProduct(payments);
    // const isEachProductCovered = Object.values(totalPercentagePerProduct).every(
    //     (percentage) => percentage === 100,
    // );
    const areIndividualPaymentsValid = payments.filter(p=>!isFinalSettlementPayment(p)).every((p) => isSinglePaymentValid(p).isValid);

    const isValid = isAtLeastOneTranche && isEachProductCovered && areIndividualPaymentsValid;

    return {
        isValid,
        isEachProductCovered,
        areIndividualPaymentsValid,
        isAtLeastOneTranche,
    };
};

export const useIsAgreementFormDataValid = (fields) => {
    const agreementType = useWatch({ name: fields.agreementType });
    const productDiscounts = useWatch({ name: fields.productDiscounts });
    const customerIds = useWatch({ name: fields.customerIds });
    const customerShares = useWatch({ name: fields.customerShares });
    const payments = useWatch({ name: fields.payments });
    const caretakerId = useWatch({ name: fields.caretakerId });

    const selectedProductIds = Object.keys(productDiscounts || {});
    const { data: selectedProducts, isLoading } = useProductsByIds(selectedProductIds);
    const { data: customers, isLoading: isLoadingCustomers } = properUseCustomersByIds(customerIds);

    const isAgreementTypeValid =
        agreementType != null && Object.values(AgreementKindEnum).includes(agreementType);

    const isAtLeastOneCustomerSelected = customerIds.length > 0;

    const areCustomerSharesValid = areSharesValid(customerShares, customerIds);

    const allCustomersHaveRequiredInfo =
        !isLoadingCustomers && customers != null && customers.every(customerHasRequiredInfo);

    const areAgreementDetailsValid = true; // TODO details validation logic

    const isAtLeastOneApartmentSelected =
        selectedProductIds.length > 0 &&
        !isLoading &&
        selectedProducts != null &&
        selectedProducts.some((p) => p.is_apartment);

    // const areAllPaymentsValid = arePaymentsValid(payments).isValid;
    const areAllPaymentsValid = arePaymentsValid(payments, selectedProducts, productDiscounts).isValid;

    const isValid =
        caretakerId &&
        isAgreementTypeValid &&
        isAtLeastOneCustomerSelected &&
        areCustomerSharesValid &&
        allCustomersHaveRequiredInfo &&
        areAgreementDetailsValid &&
        isAtLeastOneApartmentSelected &&
        areAllPaymentsValid;

    return {
        isValid,
        isAgreementTypeValid,
        isCustomerSelectionValid: isAtLeastOneCustomerSelected,
        areCustomerSharesValid,
        allCustomersHaveRequiredInfo,
        isCustomerInfoValid: areCustomerSharesValid && allCustomersHaveRequiredInfo,
        areAgreementDetailsValid,
        isProductSelectionValid: isAtLeastOneApartmentSelected,
        areAllPaymentsValid,
    };
};
